import React, { useRef, useState, useCallback } from "react";
import { Spinner, Alert } from "react-bootstrap";
import { BsTrash, BsCloudUploadFill, BsFileEarmarkPdf, BsFileEarmarkImage, BsFileEarmarkExcel, BsFileEarmarkWord, BsFileEarmarkText, BsFileEarmark } from "react-icons/bs";
import { useClientFiles, useUploadClientFile, useDeleteClientFile, useDownloadClientFile } from "../../../hooks/useClientFiles";
import { toast } from "react-toastify";

const ALLOWED_TYPES = [
  "image/jpeg", "image/png", "image/gif", "image/webp",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/plain", "text/csv",
];
const MAX_MB = 10;

const EXT_ICON_MAP = {
  pdf:  <BsFileEarmarkPdf  className="text-danger"  size={16} />,
  jpg:  <BsFileEarmarkImage className="text-primary" size={16} />,
  jpeg: <BsFileEarmarkImage className="text-primary" size={16} />,
  png:  <BsFileEarmarkImage className="text-primary" size={16} />,
  gif:  <BsFileEarmarkImage className="text-primary" size={16} />,
  webp: <BsFileEarmarkImage className="text-primary" size={16} />,
  xls:  <BsFileEarmarkExcel className="text-success" size={16} />,
  xlsx: <BsFileEarmarkExcel className="text-success" size={16} />,
  doc:  <BsFileEarmarkWord  className="text-primary" size={16} />,
  docx: <BsFileEarmarkWord  className="text-primary" size={16} />,
  csv:  <BsFileEarmarkText  className="text-success" size={16} />,
  txt:  <BsFileEarmarkText  className="text-muted"   size={16} />,
};
const getIcon = (name = "") => EXT_ICON_MAP[name.split(".").pop().toLowerCase()] ?? <BsFileEarmark className="text-muted" size={16} />;
const humanSize = (b) => !b ? "" : b < 1024 ? `${b} B` : b < 1048576 ? `${(b/1024).toFixed(1)} Ko` : `${(b/1048576).toFixed(1)} Mo`;

/**
 * ClientFiles
 * @param {number}  clientId   — id du client courant
 * @param {boolean} canUpload  — isActive && hasPermission('EDIT_CLIENT')
 * @param {boolean} canDelete  — isActive && hasPermission('EDIT_CLIENT')
 */
const ClientFiles = ({ clientId, canUpload = false, canDelete = false }) => {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const { data: rawFiles, isLoading, isError } = useClientFiles(clientId);
  const files = Array.isArray(rawFiles) ? rawFiles : [];
  const upload = useUploadClientFile(clientId);
  const remove = useDeleteClientFile(clientId);
  const { view } = useDownloadClientFile(clientId);

  // ── validation ──────────────────────────────────────────────────────────────
  const validate = (file) => {
    if (!ALLOWED_TYPES.includes(file.type))
      return `Type non autorisé : ${file.name}`;
    if (file.size > MAX_MB * 1024 * 1024)
      return `Fichier trop lourd (max ${MAX_MB} Mo)`;
    return null;
  };

  // ── upload ──────────────────────────────────────────────────────────────────
  const handleFiles = useCallback((fileList) => {
    const file = fileList[0];
    if (!file) return;
    const err = validate(file);
    if (err) { toast.error(err); return; }
    upload.mutate(file, {
      onSuccess: () => toast.success("Fichier ajouté !"),
      onError:   () => toast.error("Erreur lors de l'envoi du fichier."),
    });
  }, [upload]);

  // ── drag & drop ─────────────────────────────────────────────────────────────
  const onDragOver  = (e) => { e.preventDefault(); setDragOver(true); };
  const onDragLeave = ()  => setDragOver(false);
  const onDrop      = (e) => { e.preventDefault(); setDragOver(false); if (canUpload) handleFiles(e.dataTransfer.files); };

  // ── delete ──────────────────────────────────────────────────────────────────
  const handleDelete = (fileId) => {
    setDeletingId(fileId);
    remove.mutate(fileId, {
      onSuccess: () => toast.success("Fichier supprimé !"),
      onError:   () => toast.error("Erreur lors de la suppression."),
      onSettled: () => setDeletingId(null),
    });
  };

  return (
    <div>
      {/* ── Drop zone (visible uniquement si éditable) ── */}
      {canUpload && (
        <div
          className={`rounded p-3 text-center mb-3 border ${dragOver ? "border-primary bg-primary bg-opacity-10" : "border-secondary border-opacity-25"}`}
          style={{ borderStyle: "dashed !important", cursor: "pointer", transition: "all .15s" }}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          onClick={() => !upload.isPending && inputRef.current?.click()}
        >
          <input
            ref={inputRef}
            type="file"
            className="d-none"
            accept={ALLOWED_TYPES.join(",")}
            onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
          />
          {upload.isPending ? (
            <Spinner size="sm" animation="border" className="primary" />
          ) : (
            <>
              <BsCloudUploadFill size={20} className="text-primary mb-1" />
              <div style={{ fontSize: "0.78rem" }} className="text-muted">
                Glissez un fichier ou{" "}
                <span className="text-primary fw-semibold">parcourir</span>
                <br />
                <span style={{ fontSize: "0.7rem" }}>PDF, Word, Excel, images — max {MAX_MB} Mo</span>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── États ── */}
      {isError && <Alert variant="danger" className="py-2 small mb-2">Erreur de chargement</Alert>}
      {isLoading && <div className="text-center py-2"><Spinner size="sm" animation="border" style={{ color: "#4e73df" }} /></div>}

      {/* ── Liste des fichiers ── */}
      {!isLoading && (
        files.length === 0 ? (
          <p className="text-muted small mb-0">Aucun fichier joint.</p>
        ) : (
          <ul className="list-unstyled mb-0">
            {files.map((file) => (
              <li key={file.id} className="d-flex align-items-center justify-content-between py-1 border-bottom">
                <div className="d-flex align-items-center gap-2 text-truncate" style={{ minWidth: 0 }}>
                  <span>{getIcon(file.name)}</span>
                  <span className="text-truncate small" title={file.name} style={{ maxWidth: 160 }}>
                  </span>
                  <button
                      className="btn btn-link p-0 text-truncate small text-decoration-none"
                      style={{ maxWidth: 160, cursor: "pointer" }}
                      onClick={() => view(file.id)} // On utilise view au lieu de download
                    >
                      {file.name}
                    </button>
                  {file.size && (
                    <span className="text-muted" style={{ fontSize: "0.7rem", whiteSpace: "nowrap" }}>
                      {humanSize(file.size)}
                    </span>
                  )}
                </div>
                {canDelete && (
                  <button
                    className="btn btn-link btn-sm text-danger p-0 ms-2 flex-shrink-0"
                    title="Supprimer"
                    disabled={deletingId === file.id}
                    onClick={() => handleDelete(file.id)}
                  >
                    {deletingId === file.id
                      ? <Spinner size="sm" animation="border" />
                      : <BsTrash size={13} />
                    }
                  </button>
                )}
              </li>
            ))}
          </ul>
        )
      )}
    </div>
  );
};

export default ClientFiles;